/**
 * What each session in the list is DOING (RFC 0286 S7).
 *
 * Replaces `sessionActivity`, which watched only the sessions this client had
 * already OPENED. That was an honest under-report and its own header said so: a
 * run started on another device or from the CLI never lit its row, which is the
 * exact case a person keeping several sessions cares about most. Watching what
 * you happen to have open answers "is this tab busy", not "what is running".
 *
 * So this OPENS the rows it is asked about, and the cost is bounded by the
 * caller passing only the ids it is showing. That is the same bargain
 * `list_sessions` strikes with `withState`: the registry read stays free (RFC
 * 0233 zero-hydration, membership and titles with nothing opened), and status is
 * an opt-in that pays a document per row.
 *
 * SINCE RFC 0284 S8a IT IS THE FALLBACK, not the only path. A runtime mirrors
 * status onto the registry, so most rows are answered by one document read and
 * this opens only the ones the registry could not answer: a session nobody
 * reported on, or one whose claim went stale because its writer died. Pass
 * those ids and no others.
 *
 * The cap stays until the mirror is written by every runtime. A list that
 * silently under-reports because a CLI session was not counted is worse than
 * one that admits it opens twenty rows.
 *
 * The status itself is mod-core's `projectSessionStatus`, not a second
 * definition of "working" living in the client.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { projectSessionStatus, type SessionRunStatus } from '@mod/core/model'
import { useEffect, useState } from 'react'
import { holdDocument } from '../world/repo/held-docs'

/**
 * How many rows the list will open to report status.
 *
 * Matches the tool's cap so both surfaces claim the same coverage. More than
 * anyone is steering at once, and small enough that a project which has
 * accumulated hundreds of sessions does not hydrate them all to draw a list.
 */
export const STATUS_ROW_LIMIT = 20

/**
 * How long a burst of changes to one session is allowed to gather before its
 * status is re-read. Short enough that a run starting still lights the row as
 * it happens, long enough that a sync catch-up is one read rather than one per
 * patch.
 */
const STATUS_COALESCE_MS = 250

/** Status per session id. A missing entry is UNKNOWN, never idle: the row was
 *  not read, and reporting it as quiet is how a stuck session gets skipped. */
export type SessionStatusMap = ReadonlyMap<string, SessionRunStatus>

/**
 * Watch the given sessions, most-important-first, up to the cap.
 *
 * Handles are cached by the repo, so re-rendering with the same ids re-attaches
 * listeners to resident docs rather than refetching.
 */
export function useSessionStatuses(
  repo: Repo | undefined,
  sessionIds: readonly string[],
): SessionStatusMap {
  const [statuses, setStatuses] = useState<SessionStatusMap>(new Map())
  // The ids are a fresh array every render; join them so the effect re-runs when
  // the SET changes, not on every parent render.
  const key = sessionIds.slice(0, STATUS_ROW_LIMIT).join(',')

  useEffect(() => {
    if (!repo || key === '') {
      setStatuses(new Map())
      return
    }
    const ids = key.split(',')
    let live = true
    const cleanups: Array<() => void> = []
    // One map mutated by many listeners, republished as a NEW map on each
    // change: useState compares by reference, so mutating in place would render
    // nothing.
    const current = new Map<string, SessionRunStatus>()

    for (const id of ids) {
      // Watched until the effect ends, so held until then (RFC 0317 §3d).
      cleanups.push(holdDocument(repo as Repo, id))
      void repo
        .find(id as AnyDocumentId)
        .then((handle: DocHandle<unknown>) => {
          if (!live) return
          const read = () => {
            const next = projectSessionStatus(handle.doc())
            if (current.get(id) === next) return
            current.set(id, next)
            setStatuses(new Map(current))
          }
          /**
           * COALESCE THE READS. `change` fires per sync patch, and a session
           * that is catching up delivers hundreds of them in a row; the status
           * only has to be right about where the doc ENDED, so recomputing on
           * each one is pure waste. It is not cheap waste either: the
           * projection walks the whole log, so twenty rows re-reading a large
           * session on every patch was enough garbage to take a renderer past
           * its heap limit before the list had finished drawing.
           *
           * A timeout rather than a frame: this must settle while the tab is in
           * the background too, where frames stop coming.
           */
          let pending: ReturnType<typeof setTimeout> | null = null
          const onChange = () => {
            if (pending !== null) return
            pending = setTimeout(() => {
              pending = null
              if (live) read()
            }, STATUS_COALESCE_MS)
          }
          handle.on('change', onChange)
          cleanups.push(() => {
            handle.off('change', onChange)
            if (pending !== null) clearTimeout(pending)
          })
          read()
        })
        .catch(() => {
          // Unreachable: leave it out of the map so it reads as unknown. A
          // session nobody can open is not a session doing nothing.
        })
    }

    return () => {
      live = false
      for (const off of cleanups) off()
    }
  }, [repo, key])

  return statuses
}
